<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES, MESSAGE_STATUS } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType, status, thinkingContent } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const showThinking = ref(true);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isStreaming = computed(() => {
  return status.value === MESSAGE_STATUS.STREAMING;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length && !isStreaming.value;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <!-- Thinking section: v-show keeps DOM position fixed at mount time -->
      <div v-show="thinkingContent" class="mb-2">
        <button
          class="flex items-center gap-1.5 text-xs text-n-slate-10 dark:text-n-slate-5 hover:text-n-slate-12 dark:hover:text-n-slate-3 transition-colors"
          @click="showThinking = !showThinking"
        >
          <span
            class="inline-block transition-transform duration-200"
            :class="showThinking ? 'rotate-90' : ''"
          >&#9654;</span>
          AI 思考过程
        </button>
        <div
          v-if="showThinking"
          class="mt-1.5 p-2.5 rounded-lg bg-n-slate-2 dark:bg-n-slate-9 text-xs text-n-slate-11 dark:text-n-slate-4 whitespace-pre-wrap max-h-60 overflow-y-auto"
        >
          {{ thinkingContent }}
        </div>
      </div>
      <!-- Streaming typing indicator -->
      <div v-if="isStreaming && !renderContent && !thinkingContent" class="flex items-center gap-1 py-1">
        <span class="w-2 h-2 rounded-full bg-n-slate-8 dark:bg-n-slate-4 animate-bounce [animation-delay:-0.3s]" />
        <span class="w-2 h-2 rounded-full bg-n-slate-8 dark:bg-n-slate-4 animate-bounce [animation-delay:-0.15s]" />
        <span class="w-2 h-2 rounded-full bg-n-slate-8 dark:bg-n-slate-4 animate-bounce" />
      </div>
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
